import React, { useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useMediaQuery } from "react-responsive";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Components
import TimelineSubmitForm from "components/assessment-form/TimelineSubmitForm";
import BasicInfoForm from "components/assessment-form/BasicInfoForm";
import BasicInfoSummary from "components/assessment-form/BasicInfoSummary";
import AssessmenInstructions from "components/assessment-form/AssessmentInstructions";
import AssessmentResults from "components/assessment-form/AssessmentResults";
import QuestionnaireForm from "components/assessment-form/QuestionnaireForm";
import ModalConfirmation from "components/modal/ModalConfirmation";

// Data
import {
  dpm_assessment_form,
  adpm_assessment_form,
  dc_assessment_form,
  dirm_lvl5_assessment_form,
  dirm_lvl4_assessment_form,
  pcp_assessment_form,
  dpm_assessment_result,
  adpm_assessment_result,
  dc_assessment_result,
  dirm_lvl5_assessment_result,
  dirm_lvl4_assessment_result,
  pcp_assessment_result,
} from "utils/constants/data";

// Apis
import useFetch from "utils/hooks/useFetch";
import {
  GET_COURSES_FOR_ASSESSMENT,
  CREATE_CANDIDATE_SUBMISSION,
  CALCULATE_RESULT_SUBMISSION,
} from "api";

// Helper
import { selfAssessmentResultTemplate } from "utils/template/selfAssessmentResultTemplate";
import { dataURLToBlob, generateAssessmentUUID } from "utils/helpers";
import {
  capitalizeEachWord,
  capitalizeFirstLetter,
  formatAssessmentOptionCourses,
} from "utils/helpers";

function AssessmentForm() {
  const signatureRef = useRef(null);
  const smallScreen = useMediaQuery({ query: "(max-width: 767px)" });

  const { fetch: getAllCourses, error: errorGetAllCourses } = useFetch(
    GET_COURSES_FOR_ASSESSMENT,
  );
  const {
    fetch: sendCandidateAssessmentForm,
    error: errorSendCandidateAssessmentForm,
  } = useFetch(CREATE_CANDIDATE_SUBMISSION);
  const {
    fetch: sendCalculateResultForm,
    error: errorSendCalculateResultForm,
  } = useFetch(CALCULATE_RESULT_SUBMISSION);

  const initialValues = {
    full_name: "",
    email_address: "",
    phone_code: "",
    phone_number: "",
    company_name: "",
    job_title: "",
    course_id: "",
    selected_program: "",
    signature: "",
  };

  const validationSchemaBasicInfo = Yup.object({
    full_name: Yup.string().required("Full name is required"),
    email_address: Yup.string()
      .required("Email address is required")
      .matches(
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9-]{2,6}$/,
        "Invalid email format",
      ),
    phone_number: Yup.number()
      .typeError("Invalid number format")
      .required("Phone number is required"),
    phone_code: Yup.mixed().required("Country code is required"),
    course_id: Yup.mixed().required("Select program is required"),
  });

  const validationSchemaQuestionnaire = Yup.object({
    signature: Yup.mixed().required("Please provide a valid signature"),
  });

  const [coursesOptions, setCoursesOptions] = useState([]);
  const [formSection, setFormSection] = useState("basic-info");
  const [isSelfAssessmentStart, setSelfAssesmentStart] = useState(false);
  const [openTimerIsUp, setOpenTimerIsUp] = useState(false);
  const [openBasicInfoConfirmation, setOpenBasicInfoConfirmation] =
    useState(false);
  const [openReloadConfirmation, setOpenReloadConfirmation] = useState(false);
  const [openFormNotCompleted, setOpenFormNotCompleted] = useState(false);
  const [assessmentQuestionnaire, setAssessmentQuestionnaire] = useState([]);
  const [initialRecommendationResult, setInitialRecommendationResult] =
    useState({});
  const [finalInitialRecommendation, setFinalInitialRecommendation] =
    useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formIsSubmit, setFormIsSubmit] = useState(false);
  const [totalOptionA, setTotalOptionA] = useState(0);
  const [totalOptionB, setTotalOptionB] = useState(0);
  const [totalOptionC, setTotalOptionC] = useState(0);
  const [totalOptionD, setTotalOptionD] = useState(0);
  const [totalScore, setTotalScore] = useState(0);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    enableReinitialize: true,
    initialValues,
    ...(formSection === "basic-info"
      ? {
          validationSchema: validationSchemaBasicInfo,
        }
      : formSection === "assessment-form"
        ? {
            validationSchema: validationSchemaQuestionnaire,
          }
        : {}),
    onSubmit: async (values) => {
      if (formSection === "basic-info") {
        handleCourseAssessmentGridForm();
        setOpenBasicInfoConfirmation(true);
      } else if (formSection === "assessment-form") {
        const gridFormIsValid = handleGridFormValidation();
        if (gridFormIsValid) {
          await handleSubmitCandidateAssessment(values);
        } else {
          setOpenFormNotCompleted(true);
        }
      }
    },
  });

  useEffect(() => {
    if (values) {
      let option_A = 0;
      let option_B = 0;
      let option_C = 0;
      let option_D = 0;

      for (var key in values) {
        if (values.hasOwnProperty(key)) {
          if (values[key] === "A") {
            option_A += 1;
          }
          if (values[key] === "B") {
            option_B += 1;
          }
          if (values[key] === "C") {
            option_C += 1;
          }
          if (values[key] === "D") {
            option_D += 1;
          }
        }
      }

      setTotalOptionA(option_A);
      setTotalOptionB(option_B);
      setTotalOptionC(option_C);
      setTotalOptionD(option_D);
    }
  }, [values]);

  useEffect(() => {
    if (formSection === "assessment-form") {
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [formSection]);

  useEffect(() => {
    getAllCourses().then((data) => {
      if (data && data.success && data.success === true) {
        const formattedData = formatAssessmentOptionCourses(data.response);
        setCoursesOptions(formattedData);
      }
    });
  }, [getAllCourses]);

  useEffect(() => {
    if (errorGetAllCourses && errorGetAllCourses.success === false) {
      const errMessage =
        (errorGetAllCourses && errorGetAllCourses.message) ||
        "Failed to fetch courses";
      toast.error(<p className="p-2 text-sm lg:text-base">{errMessage}</p>);
    }

    if (
      errorSendCandidateAssessmentForm &&
      errorSendCandidateAssessmentForm.success === false
    ) {
      const errMessage =
        (errorSendCandidateAssessmentForm &&
          errorSendCandidateAssessmentForm.message) ||
        "There is something wrong when submitting your evaluation. Please try again later.";
      setIsSubmitting(false);
      toast.error(<p className="p-2 text-sm lg:text-base">{errMessage}</p>);
    }

    if (
      errorSendCalculateResultForm &&
      errorSendCalculateResultForm.success === false
    ) {
      const errMessage =
        (errorSendCalculateResultForm &&
          errorSendCalculateResultForm.message) ||
        "There is something wrong when calculating your result. Please try again later.";
      setIsSubmitting(false);
      toast.error(<p className="p-2 text-sm lg:text-base">{errMessage}</p>);
    }
  }, [
    errorGetAllCourses,
    errorSendCandidateAssessmentForm,
    errorSendCalculateResultForm,
  ]);

  const handleCourseAssessmentGridForm = () => {
    let assessment_form = [];

    if (values.course_id == 1) {
      assessment_form = dpm_assessment_form;
      setAssessmentQuestionnaire(dpm_assessment_form);
      setInitialRecommendationResult(dpm_assessment_result);
    }

    if (values.course_id == 2) {
      assessment_form = adpm_assessment_form;
      setAssessmentQuestionnaire(adpm_assessment_form);
      setInitialRecommendationResult(adpm_assessment_result);
    }

    if (values.course_id == 3) {
      assessment_form = dc_assessment_form;
      setAssessmentQuestionnaire(dc_assessment_form);
      setInitialRecommendationResult(dc_assessment_result);
    }

    if (values.course_id == 4) {
      assessment_form = dirm_lvl5_assessment_form;
      setAssessmentQuestionnaire(dirm_lvl5_assessment_form);
      setInitialRecommendationResult(dirm_lvl5_assessment_result);
    }

    if (values.course_id == 5) {
      assessment_form = dirm_lvl4_assessment_form;
      setAssessmentQuestionnaire(dirm_lvl4_assessment_form);
      setInitialRecommendationResult(dirm_lvl4_assessment_result);
    }

    if (values.course_id == 6) {
      assessment_form = pcp_assessment_form;
      setAssessmentQuestionnaire(pcp_assessment_form);
      setInitialRecommendationResult(pcp_assessment_result);
    }

    if (
      assessment_form &&
      assessment_form.list &&
      assessment_form.list.length > 0
    ) {
      let questionnaireObj = {};
      for (let i = 0; i < assessment_form.list.length; i++) {
        const list = assessment_form.list[i];
        for (let j = 0; j < list.description.length; j++) {
          questionnaireObj[`subject_${i}_description_${j}`] = "";
        }
      }
      setValues(
        (values) => ({
          ...values,
          ...questionnaireObj,
        }),
        false,
      );
    }
  };

  const handleSubmitCandidateAssessment = async (values) => {
    setIsSubmitting(true);
    setFormIsSubmit(true);
    setSelfAssesmentStart(false);

    const calculationResult = await handleCalculation(values);
    const inputValues = handleInputValue(values, calculationResult);
    const newAssessmentSubmission = await sendCandidateAssessmentForm({
      data: inputValues,
    });

    if (newAssessmentSubmission && newAssessmentSubmission.success) {
      setIsSubmitting(false);
      setFormSection("submitted-form");
    }
  };

  const handleInputValue = (values, calculationResult) => {
    const formData = new FormData();

    const blobCandidateSignature = dataURLToBlob(values.signature);
    const assessmentUUID = generateAssessmentUUID(
      values.selected_program,
      values.signature,
    );
    const htmlSelfAssessment = selfAssessmentResultTemplate(
      values,
      assessmentQuestionnaire,
      calculationResult && calculationResult.initial_recommendation,
      calculationResult && calculationResult.total_score,
      calculationResult && calculationResult.total_option_A,
      calculationResult && calculationResult.total_option_B,
      calculationResult && calculationResult.total_option_C,
      calculationResult && calculationResult.total_option_D,
    );

    formData.append("full_name", capitalizeEachWord(values.full_name.trim()));
    formData.append("email_address", values.email_address.toLowerCase().trim());
    formData.append(
      "phone_number",
      `${values.phone_code.value}-${values.phone_number}`,
    );
    formData.append("company_name", capitalizeFirstLetter(values.company_name));
    formData.append("job_title", capitalizeFirstLetter(values.job_title));
    formData.append("selected_program", values.selected_program);
    formData.append("course_id", values.course_id);
    formData.append(
      "assessment_score",
      calculationResult && calculationResult.total_score,
    );
    formData.append(
      "assessment_recommendation",
      calculationResult && calculationResult.initial_recommendation,
    );

    formData.append(
      "file",
      blobCandidateSignature,
      "candidate-signature-assessment.png",
    );
    formData.append("assessment_uuid", assessmentUUID);
    formData.append("assessment_date", new Date());
    formData.append("template_assessment_result", htmlSelfAssessment);

    return formData;
  };

  const handleCalculation = async (values) => {
    const payload = {
      course_id: values.course_id,
      option_A: totalOptionA,
      option_B: totalOptionB,
      option_C: totalOptionC,
      option_D: totalOptionD,
    };

    const submissionResult = await sendCalculateResultForm({ data: payload });
    if (
      submissionResult &&
      submissionResult.total_score &&
      submissionResult.initial_recommendation
    ) {
      setTotalScore(submissionResult.total_score);
      setFinalInitialRecommendation(submissionResult.initial_recommendation);
      return submissionResult;
    }
  };

  const handleGridFormValidation = () => {
    const gridValueKeyRegex = /^subject_\d+_description_\d+$/;

    for (const key in values) {
      if (key.match(gridValueKeyRegex)) {
        const value = values[key];
        if (value === "" || value === null || value === undefined) {
          return false;
        }
      }
    }

    return true;
  };

  const handleTimerIsUp = () => {
    setOpenTimerIsUp(false);
    setSelfAssesmentStart(false);
  };

  const handleFormNotCompleted = () => {
    setOpenFormNotCompleted(false);
  };

  const handleBasicInfoConfirmation = (value) => {
    if (value) {
      setFormSection("assessment-form");
      window.scrollTo({ top: 0, behavior: "smooth" });
      setOpenBasicInfoConfirmation(false);
    } else {
      setOpenBasicInfoConfirmation(false);
    }
  };

  const handleReloadConfirmation = (value) => {
    if (value) {
      setOpenReloadConfirmation(false);
      window.location.reload();
    } else {
      setOpenReloadConfirmation(false);
    }
  };

  const handleBeforeUnload = (e) => {
    e.preventDefault();
    setOpenReloadConfirmation(true);
    e.returnValue = openReloadConfirmation;
    return openReloadConfirmation;
  };

  const handleClearSignature = () => {
    signatureRef && signatureRef.current && signatureRef.current.clear();
    setFieldValue("signature", null);
  };

  const handleSaveSignature = () => {
    const trimmedSignatureDataURL =
      signatureRef &&
      signatureRef.current &&
      signatureRef.current.getTrimmedCanvas() &&
      signatureRef.current.getTrimmedCanvas().toDataURL("image/png");
    // make sure data URL image is not a blank / empty canvas
    if (
      trimmedSignatureDataURL &&
      trimmedSignatureDataURL.split(",")[1] !==
        "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQIW2NgAAIAAAUAAR4f7BQAAAAASUVORK5CYII="
    ) {
      setFieldValue("signature", trimmedSignatureDataURL);
    }
  };

  const handleSelectedProgram = (e) => {
    const id = e.target.value;
    const selectedCourse = coursesOptions.filter((course) => {
      return course.id == id;
    })[0];

    setFieldValue("course_id", id);
    setFieldValue("selected_program", selectedCourse && selectedCourse.text);
  };

  return (
    <section>
      <div className="w-full px-6 pt-6 pb-12 mx-auto space-y-8 sm:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl lg:px-0">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={false}
          pauseOnHover={true}
          theme="colored"
          transition={Bounce}
        />
        <ModalConfirmation
          open={openTimerIsUp && formSection === "assessment-form"}
          setOpen={handleTimerIsUp}
          title={"The time is up!"}
          description={
            "Kindly begin filling out the form again, and the timer will restart from the beginning."
          }
          notifyButton
        />
        <ModalConfirmation
          open={openFormNotCompleted && formSection === "assessment-form"}
          setOpen={handleFormNotCompleted}
          title={"Form is not completed!"}
          description={
            "You have to make sure filling out the choices from all the description of every subject."
          }
          notifyButton
        />
        <ModalConfirmation
          open={openBasicInfoConfirmation && formSection === "basic-info"}
          setOpen={handleBasicInfoConfirmation}
          title={"Are you sure all the information is correct?"}
          description={
            "Before submitting, please verify that all the information you have entered is accurate."
          }
          confirmButton
          cancelButton
        />
        <ModalConfirmation
          open={openReloadConfirmation && formSection === "assessment-form"}
          setOpen={handleReloadConfirmation}
          title={"Are you sure you want to refresh page?"}
          description={
            "Your progress will be lost and you will have to start from the beginning."
          }
          confirmButton
          cancelButton
        />
        <div className="mx-4 space-y-2 text-left sm:space-y-4">
          <p className="text-2xl font-bold tracking-wide uppercase sm:text-3xl xl:text-4xl">
            Self Assessment Guide
          </p>
        </div>
        <TimelineSubmitForm
          smallScreen={smallScreen}
          formSection={formSection}
        />
        {formSection === "basic-info" && (
          <BasicInfoForm
            coursesOptions={coursesOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            handleBlur={handleBlur}
            handleSelectedProgram={handleSelectedProgram}
            handleSubmit={handleSubmit}
          />
        )}
        {(formSection === "assessment-form" ||
          formSection === "submitted-form") && (
          <BasicInfoSummary values={values} />
        )}
        {formSection === "assessment-form" &&
          !isSelfAssessmentStart &&
          !formIsSubmit && (
            <AssessmenInstructions
              setSelfAssesmentStart={setSelfAssesmentStart}
              setOpenTimerIsUp={setOpenTimerIsUp}
            />
          )}
        {((formSection === "assessment-form" && isSelfAssessmentStart) ||
          (formIsSubmit && !isSelfAssessmentStart) ||
          (formSection === "submitted-form" && !isSelfAssessmentStart)) && (
          <QuestionnaireForm
            assessmentQuestionnaire={assessmentQuestionnaire}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            handleSubmit={handleSubmit}
            totalOptionA={totalOptionA}
            totalOptionB={totalOptionB}
            totalOptionC={totalOptionC}
            totalOptionD={totalOptionD}
            formSection={formSection}
            setSelfAssesmentStart={setSelfAssesmentStart}
            isSelfAssessmentStart={isSelfAssessmentStart}
            setOpenTimerIsUp={setOpenTimerIsUp}
            isSubmitting={isSubmitting}
            formIsSubmit={formIsSubmit}
            signatureRef={signatureRef}
            handleClear={handleClearSignature}
            handleSave={handleSaveSignature}
          />
        )}
        {formSection === "submitted-form" && (
          <AssessmentResults
            finalInitialRecommendation={finalInitialRecommendation}
            totalScore={totalScore}
          />
        )}
      </div>
    </section>
  );
}

export default AssessmentForm;
